export interface WorkflowReadOptions {
  signal: AbortSignal;
  assertCurrent: () => void;
}

export class WorkflowContextReadError extends Error {
  constructor(readonly reason: 'stale' | 'unavailable') {
    super(`Workflow context ${reason}`);
  }
}

/** A real task boundary with cancellation of this read's timer and listener. */
function yieldWorkflowRead(signal: AbortSignal): Promise<void> {
  signal.throwIfAborted();
  return new Promise((resolve, reject) => {
    const finish = () => {
      signal.removeEventListener('abort', abort);
      resolve();
    };
    const timer = setTimeout(finish, 0);
    const abort = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', abort);
      reject(signal.reason);
    };
    signal.addEventListener('abort', abort, { once: true });
  });
}

/** One request-local checkpoint shared by generator and native adapter reads. */
export function createWorkflowReadCheckpoint({
  signal,
  assertCurrent,
}: WorkflowReadOptions): () => Promise<void> {
  let first = true;
  let count = 0;
  let deadline = 0;
  return async () => {
    signal.throwIfAborted();
    assertCurrent();
    count++;
    if (first || count >= 64 || performance.now() >= deadline) {
      await yieldWorkflowRead(signal);
      signal.throwIfAborted();
      assertCurrent();
      first = false;
      count = 0;
      deadline = performance.now() + 4;
    }
  };
}

/** Bound native record work, including skipped records, before the next task. */
export async function materializeWorkflowRead<T>(
  work: Generator<void, T>,
  options: WorkflowReadOptions
): Promise<T> {
  const checkpoint = createWorkflowReadCheckpoint(options);
  try {
    // Give Stop and pending native model publication a task before capture.
    await checkpoint();
    while (true) {
      options.signal.throwIfAborted();
      const next = work.next();
      if (next.done) {
        options.signal.throwIfAborted();
        options.assertCurrent();
        return next.value;
      }
      await checkpoint();
    }
  } finally {
    work.return(undefined as T);
  }
}
